import { DateTime } from "luxon";
import Papa from "papaparse";

// Sort array by property
export const sortArrayByProperty = (data, property, order = "ascending") => {
  return [...data].sort((a, b) => {
    let aValue = a[property];
    let bValue = b[property];

    if (aValue === null || aValue === undefined) aValue = "";
    if (bValue === null || bValue === undefined) bValue = "";

    if (typeof aValue === "string") aValue = aValue.toLowerCase();
    if (typeof bValue === "string") bValue = bValue.toLowerCase();

    if (aValue < bValue) return order === "ascending" ? -1 : 1;
    if (aValue > bValue) return order === "ascending" ? 1 : -1;
    return 0;
  });
};

// Transform platform analytics data from platform_portfolio_data
export const transformPlatformAnalytics = (redGreenAnalytics) => {
  if (!redGreenAnalytics?.platform_portfolio_data) return [];

  // Aggregate platform-wise data
  const platformMap = {};
  redGreenAnalytics.platform_portfolio_data.forEach((item) => {
    if (!platformMap[item.platform]) {
      platformMap[item.platform] = {
        platform: item.platform,
        portfolio: item.portfolio, // Store portfolio for filtering
        total_apps: 0,
        app_count_red_channel: 0,
        app_count_green_channel: 0,
      };
    }
    platformMap[item.platform].total_apps += item.total_apps;
    platformMap[item.platform].app_count_red_channel += item.app_count_red_channel;
    platformMap[item.platform].app_count_green_channel += item.app_count_green_channel;
  });

  return Object.values(platformMap).map((item, index) => {
    const redPercentage =
      item.total_apps > 0
        ? parseFloat(((item.app_count_red_channel / item.total_apps) * 100).toFixed(2))
        : 0;

    return {
      id: `platform-${index}`,
      platform: item.platform || "N/A",
      portfolio: item.portfolio || "N/A", // Include portfolio for filtering
      total_apps: item.total_apps,
      red_channel: item.app_count_red_channel,
      green_channel: item.app_count_green_channel,
      red_percentage: redPercentage,
    };
  });
};

// Transform portfolio analytics data from platform_portfolio_data
export const transformPortfolioAnalytics = (redGreenAnalytics) => {
  if (!redGreenAnalytics?.platform_portfolio_data) return [];

  // Aggregate portfolio-wise data
  const portfolioMap = {};
  redGreenAnalytics.platform_portfolio_data.forEach((item) => {
    if (!portfolioMap[item.portfolio]) {
      portfolioMap[item.portfolio] = {
        portfolio: item.portfolio,
        platform: item.platform, // Store platform for filtering
        total_apps: 0,
        app_count_red_channel: 0,
        app_count_green_channel: 0,
      };
    }
    portfolioMap[item.portfolio].total_apps += item.total_apps;
    portfolioMap[item.portfolio].app_count_red_channel += item.app_count_red_channel;
    portfolioMap[item.portfolio].app_count_green_channel += item.app_count_green_channel;
  });

  return Object.values(portfolioMap).map((item, index) => {
    const redPercentage =
      item.total_apps > 0
        ? parseFloat(((item.app_count_red_channel / item.total_apps) * 100).toFixed(2))
        : 0;

    return {
      id: `portfolio-${index}`,
      portfolio: item.portfolio || "N/A",
      platform: item.platform || "N/A", // Include platform for filtering
      total_apps: item.total_apps,
      red_channel: item.app_count_red_channel,
      green_channel: item.app_count_green_channel,
      red_percentage: redPercentage,
    };
  });
};

// Get portfolios for a selected platform
export const getPortfoliosForPlatform = (redGreenAnalytics, selectedPlatform) => {
  if (!redGreenAnalytics?.platform_portfolio_data || !selectedPlatform) return [];

  return redGreenAnalytics.platform_portfolio_data
    .filter((item) => item.platform === selectedPlatform)
    .map((item, index) => {
      const redPercentage =
        item.total_apps > 0
          ? parseFloat(((item.app_count_red_channel / item.total_apps) * 100).toFixed(2))
          : 0;

      return {
        id: `portfolio-${index}`,
        portfolio: item.portfolio || "N/A",
        platform: item.platform || "N/A",
        total_apps: item.total_apps,
        red_channel: item.app_count_red_channel,
        green_channel: item.app_count_green_channel,
        red_percentage: redPercentage,
      };
    });
};

// Get platforms for a selected portfolio
export const getPlatformsForPortfolio = (redGreenAnalytics, selectedPortfolio) => {
  if (!redGreenAnalytics?.platform_portfolio_data || !selectedPortfolio) return [];

  return redGreenAnalytics.platform_portfolio_data
    .filter((item) => item.portfolio === selectedPortfolio)
    .map((item, index) => {
      const redPercentage =
        item.total_apps > 0
          ? parseFloat(((item.app_count_red_channel / item.total_apps) * 100).toFixed(2))
          : 0;

      return {
        id: `platform-${index}`,
        platform: item.platform || "N/A",
        portfolio: item.portfolio || "N/A",
        total_apps: item.total_apps,
        red_channel: item.app_count_red_channel,
        green_channel: item.app_count_green_channel,
        red_percentage: redPercentage,
      };
    });
};

// Filter channel data based on active tab
export const filterChannelData = (redGreenChannelData, activeChannelTab) => {
  if (!Array.isArray(redGreenChannelData)) return [];

  if (activeChannelTab === "Red Channel") {
    return redGreenChannelData.filter((item) => item.status === "Red");
  } else if (activeChannelTab === "Moved to Green Channel") {
    return redGreenChannelData.filter((item) => item.status === "Green");
  }
  return redGreenChannelData;
};

// Transform channel data for table display
export const transformChannelData = (paginatedData, sortColumn, sortDirection) => {
  const sortedData = sortArrayByProperty(paginatedData, sortColumn, sortDirection);

  return sortedData.map((item, index) => {
    const formatDate = (dateValue) => {
      if (!dateValue) return "-";
      const date = DateTime.fromISO(dateValue);
      return date.isValid ? date.toFormat("dd-MM-yyyy") : "-";
    };

    return {
      id: `channel-${item.app_id}-${index}`,
      app_id: item.app_id || "-",
      app_name: item.app_name || "-",
      platform: item.platform || "-",
      portfolio: item.portfolio || "-",
      status: item.status || "-",
      trigger_type: item.trigger_type || "-",
      latest_incident: item.latest_incident || "-",
      priority: item.priority || "-",
      latest_incident_date: item.latest_incident_date || "-",
      added_to_red_channel: formatDate(item.added_to_red_channel),
      moved_to_green_channel_on: formatDate(item.moved_to_green_channel_on),
      final_review: formatDate(item.final_review),
      deployment_type: item.deployment_type || "-",
      deployment_details: item.deployment_details || "-",
      comments: item.comments || "-",
      _originalData: item,
    };
  });
};

// Download CSV helper
export const downloadCSV = (data, filename) => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Format data for download
export const formatDataForDownload = (data, activeChannelTab) => {
  return data.map((item) => {
    const formatDate = (dateValue) => {
      if (!dateValue) return "";
      const date = DateTime.fromISO(dateValue);
      return date.isValid ? date.toFormat("dd-MM-yyyy") : "";
    };

    // Use _originalData for dates to get the raw ISO format
    const originalData = item._originalData || item;

    const baseData = {
      "App ID": item.app_id || "",
      "App Instance Name": item.app_name || "",
      Platform: item.platform || "",
      Portfolio: item.portfolio || "",
      Status: item.status || "",
      Trigger: item.trigger_type || "",
      "Latest Incident": item.latest_incident || "",
      Priority: item.priority || "",
      "Latest Incident Date": item.latest_incident_date || "",
      "Added to Red Channel": formatDate(originalData.added_to_red_channel),
    };

    // Only include "Moved to Green Channel" if not Red Channel tab
    if (activeChannelTab !== "Red Channel") {
      baseData["Moved to Green Channel"] = formatDate(originalData.moved_to_green_channel_on);
    }

    return {
      ...baseData,
      "Final Review": formatDate(originalData.final_review),
      "Deployment Type": item.deployment_type || "",
      "Deployment Details": item.deployment_details || "",
    };
  });
};
